import { Button, Eyebrow } from '@/components/ui';
import { useTaskStore } from '@/stores/taskStore';
import { SchedulePopover } from '@/features/inbox/SchedulePopover';
import { toDateKey } from '@/lib/dateKey';
import { formatTimeFromDate } from '@/lib/time';
import type { Task } from '@/types';

function toTimeISO(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function ScheduleField({ task }: { task: Task }) {
  const updateTask = useTaskStore((s) => s.updateTask);

  if (task.status === 'inbox' || !task.scheduledStart || !task.scheduledEnd) {
    return (
      <div>
        <Eyebrow className="mb-1">Schedule</Eyebrow>
        <div className="flex items-center gap-2">
          <span className="text-body text-t3">Not scheduled</span>
          <SchedulePopover
            task={task}
            trigger={
              <Button variant="secondary" size="sm">
                Schedule it…
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  const start = task.scheduledStart;
  const end = task.scheduledEnd;

  const onDateChange = (newDate: string) => {
    // Guard: only process complete ISO date strings (YYYY-MM-DD = 10 chars)
    if (newDate.length !== 10) return;
    const [y, m, d] = newDate.split('-').map(Number);
    if (!y || !m || !d) return;
    const nextStart = new Date(start);
    nextStart.setFullYear(y, m - 1, d);
    if (isNaN(nextStart.getTime())) return;
    const nextEnd = new Date(nextStart.getTime() + task.durationMinutes * 60_000);
    updateTask(task.id, { scheduledStart: nextStart, scheduledEnd: nextEnd });
  };

  const onTimeChange = (newTime: string) => {
    // <input type="time"> fires onChange with an empty/partial value mid-edit —
    // notably while the AM/PM segment is being flipped. Number('') is 0, so an
    // empty string would slip through as 00:00 and, worse, a partial parse can
    // yield NaN → setHours(NaN) → an Invalid Date. Persisting an Invalid Date
    // throws in the store serializer (storage.ts) and crashes the app. Guard the
    // shape and the finite parts, mirroring onDateChange above.
    const parts = newTime.split(':');
    if (parts.length < 2) return;
    const [h, mm] = parts.map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(mm)) return;
    const nextStart = new Date(start);
    nextStart.setHours(h, mm, 0, 0);
    if (Number.isNaN(nextStart.getTime())) return;
    const nextEnd = new Date(nextStart.getTime() + task.durationMinutes * 60_000);
    updateTask(task.id, { scheduledStart: nextStart, scheduledEnd: nextEnd });
  };

  const onClear = () => {
    updateTask(task.id, {
      status: 'inbox',
      scheduledStart: undefined,
      scheduledEnd: undefined,
    });
  };

  return (
    <div>
      <Eyebrow className="mb-1">Schedule</Eyebrow>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          key={toDateKey(start)}
          defaultValue={toDateKey(start)}
          onChange={(e) => onDateChange(e.target.value)}
          aria-label="Date"
          className="rounded-md border border-line-2 bg-bg px-2 py-1 text-caption text-t1 font-mono tabular-nums pointer-coarse:min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
        />
        <input
          type="time"
          key={toTimeISO(start)}
          defaultValue={toTimeISO(start)}
          onChange={(e) => onTimeChange(e.target.value)}
          aria-label="Time"
          className="rounded-md border border-line-2 bg-bg px-2 py-1 text-caption text-t1 font-mono tabular-nums pointer-coarse:min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
        />
        {/* whitespace-nowrap: at 320–375px the flex-wrap row breaks BEFORE this
            span, never mid-phrase between "to" and the time. */}
        <span className="whitespace-nowrap font-mono text-2xs tabular-nums text-t3">
          to {formatTimeFromDate(end)}
        </span>
        <SchedulePopover
          task={task}
          trigger={
            <Button variant="ghost" size="sm">
              Reschedule…
            </Button>
          }
        />
        <button
          type="button"
          onClick={onClear}
          className="ml-auto rounded-md px-1 text-2xs text-t3 can-hover:hover:text-t1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
        >
          Clear schedule
        </button>
      </div>
    </div>
  );
}
